import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  fetchMaterialById,
  clearSelectedEntity,
  selectSelectedMaterial,
  selectMaterialDetailStatus,
  selectMaterialDetailError,
} from '../../features/materials/materialSlice';
import { selectUserInformations } from '../../features/authSlice';
import { InputField } from '../../components/reusable/InputField';
import CourseItems from '../../components/reusable/CourseItems';
import UserItems from '../../components/reusable/UserItems';
import materialsFields from '../../config/materialsFields';
import { showToast } from '../../utils/utils';
import Loader from '../../components/Live/Loader';
import { FiArrowLeft } from 'react-icons/fi';

/**
 * Preview Material Page
 * Full page read-only view of material details
 */
const PreviewMaterialPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUserInformations);

  const selectedMaterial = useSelector(selectSelectedMaterial);
  const detailStatus = useSelector(selectMaterialDetailStatus);
  const detailError = useSelector(selectMaterialDetailError);

  useEffect(() => {
    if (id && user?.token) {
      dispatch(fetchMaterialById({ token: user.token, materialId: id }));
    }

    return () => {
      dispatch(clearSelectedEntity());
    };
  }, [id, user?.token, dispatch]);

  useEffect(() => {
    if (detailStatus === 'failed' && detailError) {
      showToast('Error', detailError, 'error');
      navigate('/dashboard/materials', { replace: true });
    }
  }, [detailStatus, detailError, navigate]);

  const loading = detailStatus === 'loading';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (detailStatus === 'failed') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{detailError}</p>
          <button
            onClick={() => navigate('/dashboard/materials')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Materials
          </button>
        </div>
      </div>
    );
  }

  if (!selectedMaterial) {
    return null;
  }

  const textFields = materialsFields.filter(field => 
    field.type === 'text' && 
    field.name !== 'course_id' && 
    field.name !== 'uploaded_by'
  );
  const textareaField = materialsFields.find(field => field.type === 'textarea');
  const courseField = materialsFields.find(field => field.name === 'course_id');
  const userField = materialsFields.find(field => field.name === 'uploaded_by');

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        staggerChildren: 0.1,
        when: "beforeChildren"
      }
    },
    exit: { opacity: 0, y: -20 }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 }
  };

  return (
    <div className="min-h-screen py-4 px-4 sm:px-6 lg:px-8">
      <div className="max-w-full mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-4">
              <Link
                to="/dashboard/materials"
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800"
              >
                <FiArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white capitalize">
                  Material Details
                </h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  View material information
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={containerVariants}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 lg:p-8 relative" style={{ overflow: 'visible' }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {textFields.map((field, index) => (
              <motion.div
                key={field.name}
                variants={itemVariants}
                transition={{ delay: index * 0.05 }}
              >
                <InputField
                  label={field.label}
                  name={field.name}
                  placeholder={field.placeholder}
                  value={selectedMaterial[field.name] || ''}
                  disabled={true}
                />
              </motion.div>
            ))}

            {courseField && (
              <motion.div variants={itemVariants}>
                <CourseItems 
                  field={courseField} 
                  formData={selectedMaterial} 
                  type="preview" 
                  disabled={true} 
                />
              </motion.div>
            )}

            {userField && (
              <motion.div variants={itemVariants}>
                <UserItems 
                  field={userField} 
                  formData={selectedMaterial} 
                  type="preview" 
                  disabled={true} 
                  accountType="material"
                />
              </motion.div>
            )}

            {textareaField && (
              <motion.div
                key={textareaField.name}
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="sm:col-span-2"
              >
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {textareaField.label}
                </label>
                <textarea
                  name={textareaField.name}
                  className="w-full h-32 p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed resize-none overflow-y-auto dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder={textareaField.placeholder}
                  value={selectedMaterial[textareaField.name] || ''}
                  disabled={true}
                />
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PreviewMaterialPage;
